import React, {
  createContext,
  useContext,
  useCallback,
  useRef,
  useState,
  ReactNode,
} from 'react';
import { useAppDispatch } from '../../app/hooks';
import { messagingActions } from '../../reducers/messagingSlice';
import { MenuLoadSource } from '../../reducers/menuSlice';
import logger from '../../utils/logger';

export interface TaskLoadingMetrics {
  acknowledgementToPageTransition?: {
    startTime: number;
    endTime?: number;
    duration?: number;
  };
  menuLoading?: {
    startTime: number;
    endTime?: number;
    duration?: number;
    source: MenuLoadSource;
  };
  taskLoadedComplete?: {
    startTime: number;
    endTime?: number;
    duration?: number;
  };
}

export interface TaskLoadingDependenciesFinishedPayload {
  taskId: string;
  sessionId: string;
  restaurantCode: string;
  agentEmail: string;
  timestamp: string;
  taskLoadedCompleteMs: number;
  acknowledgementToPageTransitionMs: number;
  menuDownloadMs: number;
  menuProcessingMs: number;
  menuLoadingSource: MenuLoadSource;
  unleashFlagMs: number;
  selectRestaurantSetupMs: number;
  menuDownloadToProcessingGapMs: number;
  audioBackendConnectionMs: number;
  eventsBackendConnectionMs: number;
}

interface TaskLoadingMetricsContextType {
  startAcknowledgementToPageTransition: () => void;
  endAcknowledgementToPageTransition: () => void;
  startMenuLoading: () => void;
  endMenuLoading: (source?: MenuLoadSource) => void;
  startTaskLoadedComplete: () => void;
  endTaskLoadedComplete: () => void;
  sendTaskLoadedDependenciesFinishedMetric: (
    taskId: string,
    sessionId: string,
    restaurantCode: string,
    agentEmail: string,
    menuDownloadMs?: number,
    menuProcessingMs?: number,
    unleashFlagMs?: number,
    selectRestaurantSetupMs?: number,
    menuDownloadToProcessingGapMs?: number,
    eventWSConnectionMs?: number | null,
    audioWSConnectionMs?: number | null
  ) => void;
  resetMetrics: () => void;
  lastSentMetrics: TaskLoadingDependenciesFinishedPayload | null;
}

const TaskLoadingMetricsContext =
  createContext<TaskLoadingMetricsContextType | null>(null);

export const TaskLoadingMetricsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const dispatch = useAppDispatch();
  const metricsRef = useRef<TaskLoadingMetrics>({});
  const [lastSentMetrics, setLastSentMetrics] =
    useState<TaskLoadingDependenciesFinishedPayload | null>(null);

  const startAcknowledgementToPageTransition = useCallback(() => {
    metricsRef.current.acknowledgementToPageTransition = {
      startTime: performance.now(),
    };
  }, []);

  const endAcknowledgementToPageTransition = useCallback(() => {
    const endTime = performance.now();
    if (metricsRef.current.acknowledgementToPageTransition) {
      const startTime =
        metricsRef.current.acknowledgementToPageTransition.startTime;
      metricsRef.current.acknowledgementToPageTransition = {
        ...metricsRef.current.acknowledgementToPageTransition,
        endTime,
        duration: endTime - startTime,
      };
    }
  }, []);

  const startMenuLoading = useCallback(() => {
    metricsRef.current.menuLoading = {
      startTime: performance.now(),
      source: MenuLoadSource.DOWNLOADED,
    };
  }, []);

  const endMenuLoading = useCallback(
    (source: MenuLoadSource = MenuLoadSource.DOWNLOADED) => {
      const endTime = performance.now();
      if (metricsRef.current.menuLoading) {
        const startTime = metricsRef.current.menuLoading.startTime;
        metricsRef.current.menuLoading = {
          ...metricsRef.current.menuLoading,
          endTime,
          duration: endTime - startTime,
          source,
        };
      }
    },
    []
  );

  const startTaskLoadedComplete = useCallback(() => {
    metricsRef.current.taskLoadedComplete = { startTime: performance.now() };
  }, []);

  const endTaskLoadedComplete = useCallback(() => {
    const endTime = performance.now();
    if (metricsRef.current.taskLoadedComplete) {
      const startTime = metricsRef.current.taskLoadedComplete.startTime;
      metricsRef.current.taskLoadedComplete = {
        ...metricsRef.current.taskLoadedComplete,
        endTime,
        duration: endTime - startTime,
      };
    }
  }, []);

  const resetMetrics = useCallback(() => {
    metricsRef.current = {};
  }, []);

  const sendTaskLoadedDependenciesFinishedMetric = useCallback(
    (
      taskId: string,
      sessionId: string,
      restaurantCode: string,
      agentEmail: string,
      menuDownloadMs?: number,
      menuProcessingMs?: number,
      unleashFlagMs?: number,
      selectRestaurantSetupMs?: number,
      menuDownloadToProcessingGapMs?: number,
      eventWSConnectionMs?: number | null,
      audioWSConnectionMs?: number | null
    ) => {
      const metrics = metricsRef.current;
      if (
        metrics.acknowledgementToPageTransition?.duration === undefined ||
        metrics.taskLoadedComplete?.duration === undefined
      ) {
        logger.warn({
          message:
            'TaskLoadingMetrics: Cannot send metric - missing timing data',
          metrics,
        });
        return;
      }

      // Use passed menu timings or fallback to 0
      const finalMenuDownloadMs = menuDownloadMs ?? 0;
      const finalMenuProcessingMs = menuProcessingMs ?? 0;
      const finalUnleashFlagMs = unleashFlagMs ?? 0;
      const finalSelectRestaurantSetupMs = selectRestaurantSetupMs ?? 0;
      const finalYieldToMainMs = menuDownloadToProcessingGapMs ?? 0;
      // Use passed WS connection times from middleware or fallback to 0
      const finalEventWSConnectionMs = eventWSConnectionMs ?? 0;
      const finalAudioWSConnectionMs = audioWSConnectionMs ?? 0;

      const payload: TaskLoadingDependenciesFinishedPayload = {
        taskId,
        sessionId,
        restaurantCode,
        agentEmail,
        timestamp: new Date().toISOString(),
        taskLoadedCompleteMs: Math.round(metrics.taskLoadedComplete.duration),
        acknowledgementToPageTransitionMs: Math.round(
          metrics.acknowledgementToPageTransition.duration
        ),
        menuDownloadMs: Math.round(finalMenuDownloadMs),
        menuProcessingMs: Math.round(finalMenuProcessingMs),
        menuLoadingSource:
          metrics.menuLoading?.source || MenuLoadSource.DOWNLOADED,
        unleashFlagMs: Math.round(finalUnleashFlagMs),
        selectRestaurantSetupMs: Math.round(finalSelectRestaurantSetupMs),
        menuDownloadToProcessingGapMs: Math.round(finalYieldToMainMs),
        audioBackendConnectionMs: Math.round(finalAudioWSConnectionMs),
        eventsBackendConnectionMs: Math.round(finalEventWSConnectionMs),
      };

      logger.info({
        message: 'Task Loading Dependencies Finished Metric',
        payload,
      });

      // Send metric over events WebSocket
      dispatch(
        messagingActions.sendInfo({
          data: {
            type: 'TASK_LOADING_METRICS',
            ...payload,
          },
        } as any)
      );

      setLastSentMetrics(payload);
      resetMetrics();
    },
    [dispatch, resetMetrics]
  );

  return (
    <TaskLoadingMetricsContext.Provider
      value={{
        startAcknowledgementToPageTransition,
        endAcknowledgementToPageTransition,
        startMenuLoading,
        endMenuLoading,
        startTaskLoadedComplete,
        endTaskLoadedComplete,
        sendTaskLoadedDependenciesFinishedMetric,
        resetMetrics,
        lastSentMetrics,
      }}
    >
      {children}
    </TaskLoadingMetricsContext.Provider>
  );
};

export const useTaskLoadingMetricsContext = () => {
  const context = useContext(TaskLoadingMetricsContext);
  if (!context) {
    throw new Error(
      'useTaskLoadingMetricsContext must be used within TaskLoadingMetricsProvider'
    );
  }
  return context;
};

export default TaskLoadingMetricsProvider;
